import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  Menu, X, Plus, Search, Pencil, Trash2, MapPin, Phone, Mail,
  Eye, FileText, Clock, DollarSign, ChevronRight, Edit, Save,
  MessageCircle, Send, MailIcon, MessageSquare, TrendingUp, Scissors,
  CheckCircle, CalendarPlus, RefreshCw, Printer, XCircle, Link2
} from "lucide-react";
import { PhotoThumbs } from "@/components/PhotoThumbs";
import { useAppSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const API = "/api";

import AdminSidebar from "@/components/AdminSidebar";
import AddressMatchSuggestion from "@/components/AddressMatchSuggestion";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const Customers = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const { subscriptionsEnabled } = useAppSettings();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [customerTab, setCustomerTab] = useState("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailSubject, setEmailSubject] = useState("Zig Zag Lawncare Okinawa");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Activity logs per customer
  const [activityLogs, setActivityLogs] = useState({});
  const [expandedLog, setExpandedLog] = useState(null);
  
  // Customer details view state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerQuotes, setCustomerQuotes] = useState([]);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef(null);
  
  // Follow-up state
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    subject: "",
    message: "",
    send_email: true,
    send_sms: false,
  });
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  
  // Customer stats state
  const [customerStats, setCustomerStats] = useState(null);
  
  // Quote editing state
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteEditData, setQuoteEditData] = useState({});
  
  // Create quote state
  const [createQuoteOpen, setCreateQuoteOpen] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [newQuoteData, setNewQuoteData] = useState({
    yard_size: "medium",
    grass_height: "medium",
    customer_address: "",
    quoted_price: "",
    monthly_1x_price: "",
    monthly_2x_price: "",
    yearly_1x_price: "",
    yearly_2x_price: "",
    notes: "",
  });
  
  // Price editing dialog state
  const [priceEditOpen, setPriceEditOpen] = useState(false);
  const [priceEditCustomer, setPriceEditCustomer] = useState(null);
  const [priceEditData, setPriceEditData] = useState({
    quoted_price: "",
    monthly_1x_price: "",
    monthly_2x_price: "",
    yearly_1x_price: "",
    yearly_2x_price: "",
  });
  const [savingPrices, setSavingPrices] = useState(false);
  
  // Job editing state
  const [editingJob, setEditingJob] = useState(null);
  const [jobEditData, setJobEditData] = useState({});
  
  // Unread messages state
  const [unreadMessages, setUnreadMessages] = useState({});
  
  // Quick chat dialog state
  const [quickChatOpen, setQuickChatOpen] = useState(false);
  const [quickChatCustomer, setQuickChatCustomer] = useState(null);
  const [quickChatMessages, setQuickChatMessages] = useState([]);
  const [quickChatNewMessage, setQuickChatNewMessage] = useState("");
  const [quickChatSending, setQuickChatSending] = useState(false);
  const quickChatEndRef = useRef(null);
  
  // Subscription plans state
  const [subPlans, setSubPlans] = useState({ monthly: { discount_percent: 10 }, yearly: { discount_percent: 25 } });
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    one_time_price: "",
  });


  const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

  useEffect(() => {
    fetchCustomers();
    fetchUnreadMessages();
    // Fetch subscription plans
    axios.get(`${API}/subscription/plans`, { withCredentials: true })
      .then(res => setSubPlans(res.data))
      .catch(() => {});
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/with-quotes`, { withCredentials: true });
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = () => fetchCustomers();

  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(`${API}/customers/with-unread-messages`, { withCredentials: true });
      setUnreadMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch unread messages:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { one_time_price, ...customerPayload } = formData;
      let customerId;
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.customer_id}`, customerPayload, { withCredentials: true });
        customerId = editingCustomer.customer_id;
        toast.success("Customer updated successfully");
      } else {
        const res = await axios.post(`${API}/customers`, customerPayload, { withCredentials: true });
        customerId = res.data?.customer_id;
        toast.success("Customer added successfully");
      }

      // If a one-time price was entered, create a quote so it populates on their card
      const price = parseFloat(one_time_price);
      if (!isNaN(price) && price > 0 && customerId) {
        await axios.post(`${API}/admin/quotes/create`, {
          customer_id: customerId,
          customer_name: customerPayload.name,
          customer_email: customerPayload.email || "",
          customer_phone: customerPayload.phone || "",
          customer_address: customerPayload.address || "",
          yard_size: "unknown",
          grass_height: "unknown",
          quoted_price: price,
        }, { withCredentials: true });
        toast.success(`One-time price of $${price.toFixed(2)} set`);
      }

      setDialogOpen(false);
      setEditingCustomer(null);
      setFormData({ name: "", email: "", phone: "", address: "", notes: "", one_time_price: "" });
      fetchCustomers();
    } catch (error) {
      console.error("Failed to save customer:", error);
      toast.error(error.response?.data?.detail || "Failed to save customer");
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      notes: customer.notes || "",
      one_time_price: customer.latest_quote?.quoted_price ? String(customer.latest_quote.quoted_price) : "",
    });
    setDialogOpen(true);
  };

  // View customer details with quotes, jobs, messages, and stats
  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setViewDialogOpen(true);
    setLoadingDetails(true);
    
    try {
      // Fetch all data in parallel
      const [quotesRes, jobsRes, messagesRes, statsRes] = await Promise.all([
        axios.get(`${API}/quotes`, { withCredentials: true }),
        axios.get(`${API}/jobs`, { withCredentials: true }),
        axios.get(`${API}/customers/${customer.customer_id}/messages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/customers/${customer.customer_id}/stats`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      const custId = customer.customer_id || customer.user_id;
      const customerQuotesData = quotesRes.data.filter(q =>
        (custId && q.customer_id === custId) || (customer.email && q.customer_email === customer.email)
      );
      setCustomerQuotes(customerQuotesData);
      
      const customerJobsData = jobsRes.data.filter(j =>
        (custId && j.customer_id === custId) || (customer.email && j.customer_email === customer.email)
      );
      setCustomerJobs(customerJobsData);
      
      setMessages(messagesRes.data || []);
      setCustomerStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch customer details:", error);
      toast.error("Failed to load customer details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open customer details dialog directly to Create Quote form
  const handleOpenCreateQuote = (customer) => {
    setCreateQuoteOpen(true);
    // Pre-fill address if available
    setNewQuoteData(prev => ({ ...prev, customer_address: customer.address || "" }));
    handleViewCustomer(customer);
  };

  // Send chat message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedCustomer) return;
    
    setSendingMessage(true);
    try {
      const response = await axios.post(
        `${API}/customers/${selectedCustomer.customer_id}/messages`,
        { customer_id: selectedCustomer.customer_id, message: newMessage, message_type: "chat" },
        { withCredentials: true }
      );
      setMessages([...messages, response.data]);
      setNewMessage("");
      // Scroll to bottom
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Email dialog handlers
  const openEmailDialog = (customer) => {
    setEmailTarget(customer);
    setEmailSubject("Zig Zag Lawncare Okinawa");
    setEmailBody(`Hi ${customer.name || "there"},\n\nThanks for signing up with Zig Zag Lawncare Okinawa!\n\nSimply reply or message us with your address or just your base housing number and we will get back to you with a great quote!\n\nFor the fastest response, message us directly on Facebook Messenger:\nhttps://m.me/zigzagokinawa\n\nWe look forward to keeping your lawn looking great!\n\n— Zig Zag Lawncare Team`);
    setEmailDialogOpen(true);
  };

  const fetchActivityLog = async (customerId) => {
    try {
      const res = await axios.get(`${API}/admin/activity-log/${customerId}`, { withCredentials: true });
      setActivityLogs(prev => ({ ...prev, [customerId]: res.data }));
    } catch { /* ignore */ }
  };

  const handleActivateCustomer = async (customer) => {
    try {
      const custId = customer.customer_id || customer.user_id || "";
      // Activate by marking the customer active — does NOT create a quote request
      await axios.put(`${API}/customers/${custId}`, { customer_status: "active" }, { withCredentials: true });
      toast.success(`${customer.name} activated!`);
      fetchData();
    } catch (err) {
      console.error("Activate failed:", err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail || `Failed to activate: ${err?.message || "Unknown error"}`);
    }
  };

  const handleSetCustomerStatus = async (customerId, status) => {
    try {
      await axios.put(`${API}/customers/${customerId}`, { customer_status: status }, { withCredentials: true });
      toast.success(status === "not_interested" ? "Moved to Not Interested" : "Customer reactivated");
      fetchData();
    } catch (err) {
      console.error("Status update failed:", err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail || "Failed to update customer status");
    }
  };

  const handleGenerateReceipt = async (customer) => {
    try {
      const year = new Date().getFullYear();
      const res = await axios.get(`${API}/admin/customer-receipt/${customer.customer_id}?year=${year}`, { withCredentials: true });
      const data = res.data;
      
      if (data.jobs.length === 0) {
        toast.error(`No completed jobs found for ${customer.name} in ${year}`);
        return;
      }

      const win = window.open("", "_blank");
      win.document.write(`
        <html>
        <head>
          <title>Receipt - ${data.customer.name} - ${year}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 40px; color: #222; max-width: 800px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #39FF14; }
            .company { }
            .company h1 { font-size: 24px; color: #0A0A0A; margin-bottom: 4px; }
            .company p { color: #666; font-size: 12px; }
            .receipt-info { text-align: right; }
            .receipt-info h2 { font-size: 28px; color: #39FF14; text-transform: uppercase; letter-spacing: 2px; }
            .receipt-info p { color: #666; font-size: 13px; margin-top: 4px; }
            .customer-info { background: #f8f8f8; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
            .customer-info h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 8px; }
            .customer-info p { font-size: 14px; margin-bottom: 2px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
            th { background: #0A0A0A; color: white; text-align: left; padding: 10px 14px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
            th:last-child { text-align: right; }
            td { padding: 10px 14px; border-bottom: 1px solid #eee; font-size: 13px; }
            td:last-child { text-align: right; font-weight: bold; }
            tr:nth-child(even) { background: #fafafa; }
            .totals { display: flex; justify-content: flex-end; margin-bottom: 30px; }
            .totals-box { background: #0A0A0A; color: white; border-radius: 8px; padding: 16px 24px; min-width: 250px; }
            .totals-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 14px; }
            .totals-row.total { border-top: 1px solid #333; padding-top: 10px; margin-top: 8px; font-size: 18px; font-weight: bold; }
            .totals-row.total .amount { color: #39FF14; }
            .footer { text-align: center; color: #999; font-size: 11px; padding-top: 20px; border-top: 1px solid #eee; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              <h1>Zig Zag Lawncare Okinawa</h1>
              <p>Professional Lawn Care Services</p>
              <p>zigzaglawn@gmail.com | m.me/zigzagokinawa</p>
            </div>
            <div class="receipt-info">
              <h2>Receipt</h2>
              <p>Year: ${year}</p>
              <p>Generated: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
            </div>
          </div>

          <div class="customer-info">
            <h3>Bill To</h3>
            <p><strong>${data.customer.name}</strong></p>
            ${data.customer.address ? `<p>${data.customer.address}</p>` : ""}
            ${data.customer.email ? `<p>${data.customer.email}</p>` : ""}
            ${data.customer.phone ? `<p>${data.customer.phone}</p>` : ""}
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${data.jobs.map((j, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${new Date(j.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                  <td>${j.description || "Lawn Care Service"}${j.duration ? ` (${j.duration} min)` : ""}</td>
                  <td>$${Number(j.price).toFixed(2)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>

          <div class="totals">
            <div class="totals-box">
              <div class="totals-row">
                <span>Services Completed</span>
                <span>${data.job_count}</span>
              </div>
              <div class="totals-row total">
                <span>Total Paid</span>
                <span class="amount">$${Number(data.total).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>Thank you for choosing Zig Zag Lawncare Okinawa!</p>
            <p>This receipt is for your records. No payment is due.</p>
          </div>
        </body>
        </html>
      `);
      win.document.close();
      win.print();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate receipt");
    }
  };


  const handleSendEmail = async () => {
    if (!emailTarget?.email || !emailBody) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-email`, {
        to_email: emailTarget.email,
        customer_id: emailTarget.customer_id,
        subject: emailSubject,
        message: emailBody,
      }, { withCredentials: true });
      toast.success(`Email sent to ${emailTarget.name}!`);
      setEmailDialogOpen(false);
      // Refresh to show log
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send email";
      toast.error(detail);
    } finally {
      setSendingEmail(false);
    }
  };

  // Quick chat handlers
  const openQuickChat = async (customer) => {
    setQuickChatCustomer(customer);
    setQuickChatOpen(true);
    setQuickChatMessages([]);
    setQuickChatNewMessage("");
    
    try {
      const response = await axios.get(`${API}/customers/${customer.customer_id}/messages`, { withCredentials: true });
      setQuickChatMessages(response.data || []);
      // Mark messages as read
      await axios.put(`${API}/customers/${customer.customer_id}/messages/mark-read`, {}, { withCredentials: true });
      setUnreadMessages(prev => ({ ...prev, [customer.customer_id]: 0 }));
      setTimeout(() => quickChatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const handleQuickChatSend = async () => {
    if (!quickChatNewMessage.trim() || !quickChatCustomer) return;
    
    setQuickChatSending(true);
    try {
      const response = await axios.post(
        `${API}/customers/${quickChatCustomer.customer_id}/messages`,
        { customer_id: quickChatCustomer.customer_id, message: quickChatNewMessage, message_type: "chat" },
        { withCredentials: true }
      );
      setQuickChatMessages([...quickChatMessages, response.data]);
      setQuickChatNewMessage("");
      setTimeout(() => quickChatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setQuickChatSending(false);
    }
  };

  // Delete message from quick chat
  const handleDeleteQuickChatMessage = async (messageId) => {
    if (!quickChatCustomer || !window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(
        `${API}/customers/${quickChatCustomer.customer_id}/messages/${messageId}`,
        { withCredentials: true }
      );
      setQuickChatMessages(quickChatMessages.filter(m => m.message_id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      console.error("Failed to delete message:", error);
      toast.error("Failed to delete message");
    }
  };

  // Delete message from details modal chat
  const handleDeleteMessage = async (messageId) => {
    if (!selectedCustomer || !window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(
        `${API}/customers/${selectedCustomer.customer_id}/messages/${messageId}`,
        { withCredentials: true }
      );
      setMessages(messages.filter(m => m.message_id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      console.error("Failed to delete message:", error);
      toast.error("Failed to delete message");
    }
  };

  // Send follow-up email/SMS
  const handleSendFollowUp = async () => {
    if (!followUpData.message.trim() || !selectedCustomer) return;
    if (!followUpData.send_email && !followUpData.send_sms) {
      toast.error("Please select at least one delivery method (email or SMS)");
      return;
    }
    
    setSendingFollowUp(true);
    try {
      const response = await axios.post(
        `${API}/customers/${selectedCustomer.customer_id}/follow-up`,
        { customer_id: selectedCustomer.customer_id, ...followUpData },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
      setFollowUpDialogOpen(false);
      setFollowUpData({ subject: "", message: "", send_email: true, send_sms: false });
      // Refresh messages
      const messagesRes = await axios.get(`${API}/customers/${selectedCustomer.customer_id}/messages`, { withCredentials: true });
      setMessages(messagesRes.data || []);
    } catch (error) {
      console.error("Failed to send follow-up:", error);
      toast.error("Failed to send follow-up");
    } finally {
      setSendingFollowUp(false);
    }
  };

  // Edit quote
  const handleEditQuote = (quote) => {
    setEditingQuote(quote.quote_id);
    setQuoteEditData({
      quoted_price: quote.quoted_price || "",
      monthly_1x_price: quote.monthly_1x_price || "",
      monthly_2x_price: quote.monthly_2x_price || "",
      yearly_1x_price: quote.yearly_1x_price || "",
      yearly_2x_price: quote.yearly_2x_price || "",
      yard_size: quote.yard_size || "unknown",
      grass_height: quote.grass_height || "unknown",
      status: quote.status,
      notes: quote.notes || "",
      customer_address: quote.customer_address || "",
    });
  };

  const handleSaveQuote = async (quoteId) => {
    try {
      const saveData = {
        ...quoteEditData,
        quoted_price: quoteEditData.quoted_price && quoteEditData.quoted_price !== "N/A" ? parseFloat(quoteEditData.quoted_price) : null,
        monthly_1x_price: quoteEditData.monthly_1x_price && quoteEditData.monthly_1x_price !== "N/A" ? parseFloat(quoteEditData.monthly_1x_price) : null,
        monthly_2x_price: quoteEditData.monthly_2x_price && quoteEditData.monthly_2x_price !== "N/A" ? parseFloat(quoteEditData.monthly_2x_price) : null,
        yearly_1x_price: quoteEditData.yearly_1x_price && quoteEditData.yearly_1x_price !== "N/A" ? parseFloat(quoteEditData.yearly_1x_price) : null,
        yearly_2x_price: quoteEditData.yearly_2x_price && quoteEditData.yearly_2x_price !== "N/A" ? parseFloat(quoteEditData.yearly_2x_price) : null,
      };
      delete saveData.monthly_price;
      delete saveData.yearly_price;
      await axios.put(`${API}/quotes/${quoteId}`, saveData, { withCredentials: true });
      toast.success("Quote updated successfully");
      setEditingQuote(null);
      // Refresh quotes
      const quotesRes = await axios.get(`${API}/quotes`, { withCredentials: true });
      const custId = selectedCustomer.customer_id || selectedCustomer.user_id;
      const customerQuotesData = quotesRes.data.filter(q =>
        (custId && q.customer_id === custId) || (selectedCustomer.email && q.customer_email === selectedCustomer.email)
      );
      setCustomerQuotes(customerQuotesData);
    } catch (error) {
      console.error("Failed to update quote:", error?.response?.status, error?.response?.data);
      toast.error(error?.response?.data?.detail || "Failed to update quote");
    }
  };

  const handleCreateQuote = async () => {
    if (!selectedCustomer) return;
    setCreatingQuote(true);
    try {
      await axios.post(`${API}/admin/quotes/create`, {
        customer_id: selectedCustomer.customer_id || selectedCustomer.user_id,
        customer_name: selectedCustomer.name,
        customer_email: selectedCustomer.email || "",
        customer_phone: selectedCustomer.phone || "",
        customer_address: newQuoteData.customer_address || selectedCustomer.address || "",
        yard_size: newQuoteData.yard_size,
        grass_height: newQuoteData.grass_height,
        quoted_price: newQuoteData.quoted_price && newQuoteData.quoted_price !== "N/A" ? parseFloat(newQuoteData.quoted_price) : null,
        monthly_1x_price: newQuoteData.monthly_1x_price && newQuoteData.monthly_1x_price !== "N/A" ? parseFloat(newQuoteData.monthly_1x_price) : null,
        monthly_2x_price: newQuoteData.monthly_2x_price && newQuoteData.monthly_2x_price !== "N/A" ? parseFloat(newQuoteData.monthly_2x_price) : null,
        yearly_1x_price: newQuoteData.yearly_1x_price && newQuoteData.yearly_1x_price !== "N/A" ? parseFloat(newQuoteData.yearly_1x_price) : null,
        yearly_2x_price: newQuoteData.yearly_2x_price && newQuoteData.yearly_2x_price !== "N/A" ? parseFloat(newQuoteData.yearly_2x_price) : null,
        notes: newQuoteData.notes || null,
      }, { withCredentials: true });
      toast.success(selectedCustomer.email ? "Quote created and sent to customer!" : "Quote saved successfully!");
      setCreateQuoteOpen(false);
      setNewQuoteData({ yard_size: "medium", grass_height: "medium", customer_address: "", quoted_price: "", monthly_1x_price: "", monthly_2x_price: "", yearly_1x_price: "", yearly_2x_price: "", notes: "" });
      // Refresh quotes - filter by customer_id first, fall back to email
      const quotesRes = await axios.get(`${API}/quotes`, { withCredentials: true });
      const custId = selectedCustomer.customer_id || selectedCustomer.user_id;
      const customerQuotesData = quotesRes.data.filter(q =>
        (custId && q.customer_id === custId) || (selectedCustomer.email && q.customer_email === selectedCustomer.email)
      );
      setCustomerQuotes(customerQuotesData);
      // Refresh the chat (note becomes a message) and activity log
      if (custId) {
        try {
          const msgRes = await axios.get(`${API}/customers/${custId}/messages`, { withCredentials: true });
          setMessages(msgRes.data || []);
        } catch (e) { /* ignore */ }
        fetchActivityLog(custId);
      }
      fetchCustomers();
    } catch (error) {
      console.error("Failed to create quote:", error);
      toast.error(error.response?.data?.detail || "Failed to create quote");
    } finally {
      setCreatingQuote(false);
    }
  };

  // Open price edit dialog for a customer
  const handleEditPrices = (customer, quote) => {
    setPriceEditCustomer(customer);
    setPriceEditData({
      quoted_price: quote?.quoted_price || "",
      monthly_1x_price: quote?.monthly_1x_price || "",
      monthly_2x_price: quote?.monthly_2x_price || "",
      yearly_1x_price: quote?.yearly_1x_price || "",
      yearly_2x_price: quote?.yearly_2x_price || "",
      yard_size: quote?.yard_size || "unknown",
      grass_height: quote?.grass_height || "unknown",
      customer_address: quote?.customer_address || customer?.address || "",
      notes: quote?.notes || "",
      quote_id: quote?.quote_id || null,
    });
    setPriceEditOpen(true);
  };

  // Save edited prices
  const handleSavePrices = async () => {
    if (!priceEditData.quote_id) {
      toast.error("No quote found for this customer");
      return;
    }

    setSavingPrices(true);
    try {
      await axios.put(`${API}/quotes/${priceEditData.quote_id}`, {
        quoted_price: priceEditData.quoted_price && priceEditData.quoted_price !== "N/A" ? parseFloat(priceEditData.quoted_price) : null,
        monthly_1x_price: priceEditData.monthly_1x_price && priceEditData.monthly_1x_price !== "N/A" ? parseFloat(priceEditData.monthly_1x_price) : null,
        monthly_2x_price: priceEditData.monthly_2x_price && priceEditData.monthly_2x_price !== "N/A" ? parseFloat(priceEditData.monthly_2x_price) : null,
        yearly_1x_price: priceEditData.yearly_1x_price && priceEditData.yearly_1x_price !== "N/A" ? parseFloat(priceEditData.yearly_1x_price) : null,
        yearly_2x_price: priceEditData.yearly_2x_price && priceEditData.yearly_2x_price !== "N/A" ? parseFloat(priceEditData.yearly_2x_price) : null,
        yard_size: priceEditData.yard_size,
        grass_height: priceEditData.grass_height,
        customer_address: priceEditData.customer_address,
        notes: priceEditData.notes,
      }, { withCredentials: true });
      toast.success("Quote updated successfully");
      setPriceEditOpen(false);
      fetchCustomers(); // Refresh customer list
    } catch (error) {
      console.error("Failed to update prices:", error);
      toast.error("Failed to update prices");
    } finally {
      setSavingPrices(false);
    }
  };

  // Print customer data
  const handlePrintCustomer = (customer, quote) => {
    const yardLabels = { small: "Small (< 100 m²)", medium: "Medium (100-300 m²)", large: "Large (300-600 m²)", xlarge: "Extra Large (600+ m²)", unknown: "Unknown", "extra-large": "Extra Large (600+ m²)" };
    const grassLabels = { short: "Short (< 10cm)", medium: "Medium (10-20cm)", tall: "Tall (20-30cm)", overgrown: "Overgrown (30cm+)", unknown: "Unknown" };
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
      <head>
        <title>Customer Quote - ${customer.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; color: #111; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #39FF14; padding-bottom: 16px; margin-bottom: 24px; }
          .logo { font-size: 24px; font-weight: bold; color: #064E3B; }
          .logo span { color: #39FF14; }
          .date { color: #666; font-size: 14px; }
          h2 { font-size: 20px; margin-bottom: 16px; color: #064E3B; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
          .info-item { padding: 8px 0; }
          .info-label { font-size: 11px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; margin-bottom: 2px; }
          .info-value { font-size: 15px; font-weight: 500; }
          .pricing-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
          .price-card { border: 2px solid #ddd; border-radius: 8px; padding: 16px; text-align: center; }
          .price-card.active { border-color: #39FF14; background: #f0fff0; }
          .price-label { font-size: 12px; color: #666; margin-bottom: 4px; }
          .price-value { font-size: 22px; font-weight: bold; color: #064E3B; }
          .price-note { font-size: 11px; color: #999; margin-top: 2px; }
          .sub-status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; }
          .sub-active { background: #dcfce7; color: #166534; }
          .sub-none { background: #f3f4f6; color: #6b7280; }
          .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; text-align: center; color: #999; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">ZIG ZAG <span>LAWNCARE</span> OKINAWA</div>
          <div class="date">Printed: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
        </div>

        <h2>Customer Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">Name</div>
            <div class="info-value">${customer.name}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Email</div>
            <div class="info-value">${customer.email}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Phone</div>
            <div class="info-value">${customer.phone || "N/A"}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Address</div>
            <div class="info-value">${customer.address || quote?.customer_address || "N/A"}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Yard Size</div>
            <div class="info-value">${yardLabels[quote?.yard_size] || quote?.yard_size || "N/A"}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Grass Height</div>
            <div class="info-value">${grassLabels[quote?.grass_height] || quote?.grass_height || "N/A"}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Subscription</div>
            <div class="info-value">
              <span class="sub-status ${customer.subscription_plan !== "none" ? "sub-active" : "sub-none"}">
                ${customer.subscription_plan !== "none" ? customer.subscription_plan.toUpperCase() : "NONE"}
              </span>
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">Customer Since</div>
            <div class="info-value">${customer.created_at ? new Date(customer.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}</div>
          </div>
        </div>

        ${quote ? `
        <h2>Quote / Pricing</h2>
        <div class="pricing-grid">
          <div class="price-card${quote.quoted_price ? " active" : ""}">
            <div class="price-label">One-Time Cut</div>
            <div class="price-value">${quote.quoted_price ? "$" + quote.quoted_price.toFixed(2) : "N/A"}</div>
          </div>
          <div class="price-card">
            <div class="price-label">Monthly (1x/mo)</div>
            <div class="price-value">${quote.monthly_1x_price ? "$" + quote.monthly_1x_price.toFixed(2) : "N/A"}</div>
            ${quote.monthly_1x_price ? '<div class="price-note">per month</div>' : ""}
          </div>
          <div class="price-card">
            <div class="price-label">Monthly (2x/mo)</div>
            <div class="price-value">${quote.monthly_2x_price ? "$" + quote.monthly_2x_price.toFixed(2) : "N/A"}</div>
            ${quote.monthly_2x_price ? '<div class="price-note">per month</div>' : ""}
          </div>
          <div class="price-card">
            <div class="price-label">Yearly (1x/mo)</div>
            <div class="price-value">${quote.yearly_1x_price ? "$" + quote.yearly_1x_price.toFixed(2) : "N/A"}</div>
            ${quote.yearly_1x_price ? '<div class="price-note">per year ($' + (quote.yearly_1x_price / 12).toFixed(2) + "/mo)</div>" : ""}
          </div>
          <div class="price-card">
            <div class="price-label">Yearly (2x/mo)</div>
            <div class="price-value">${quote.yearly_2x_price ? "$" + quote.yearly_2x_price.toFixed(2) : "N/A"}</div>
            ${quote.yearly_2x_price ? '<div class="price-note">per year ($' + (quote.yearly_2x_price / 12).toFixed(2) + "/mo)</div>" : ""}
          </div>
        </div>
        ${quote.notes ? `<div style="margin-bottom:24px"><div class="info-label">Notes</div><div class="info-value">${quote.notes}</div></div>` : ""}
        ${quote.sent_at ? `<div style="margin-bottom:24px"><div class="info-label">Quote Sent</div><div class="info-value">${new Date(quote.sent_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div></div>` : ""}
        ` : '<p style="color:#999;text-align:center;padding:24px">No quote sent yet</p>'}

        <div class="footer">
          Zig Zag Lawncare Okinawa &mdash; Your Ultimate Grass Ninja
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Edit job/appointment
  const handleEditJob = (job) => {
    setEditingJob(job.job_id);
    setJobEditData({
      scheduled_date: job.scheduled_date,
      scheduled_time: job.scheduled_time,
      status: job.status,
      price: job.price || "",
      notes: job.notes || "",
    });
  };

  const handleSaveJob = async (jobId) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, jobEditData, { withCredentials: true });
      toast.success("Appointment updated successfully");
      setEditingJob(null);
      // Refresh jobs
      const jobsRes = await axios.get(`${API}/jobs`, { withCredentials: true });
      const custId = selectedCustomer.customer_id || selectedCustomer.user_id;
      const customerJobsData = jobsRes.data.filter(j =>
        (custId && j.customer_id === custId) || (selectedCustomer.email && j.customer_email === selectedCustomer.email)
      );
      setCustomerJobs(customerJobsData);
    } catch (error) {
      console.error("Failed to update appointment:", error?.response?.status, error?.response?.data);
      toast.error(error?.response?.data?.detail || "Failed to update appointment");
    }
  };

  const handleDelete = async (customerId) => {
    setCustomerToDelete(customerId);
    setDeleteDialogOpen(true);
  };

  const handleCopyInvite = async (customer) => {
    if (!customer.email) {
      toast.error("Add an email to this customer first, then send the invite");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/customers/${customer.customer_id}/invite`, {}, { withCredentials: true });
      const link = `${window.location.origin}${res.data.invite_path}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Invite link copied! Paste it into Facebook Messenger.");
      } catch {
        toast.success("Invite link ready", { description: link });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate invite link");
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;
    
    setDeletingCustomer(true);
    try {
      await axios.delete(`${API}/customers/${customerToDelete}`, { withCredentials: true });
      toast.success("Customer deleted successfully");
      fetchCustomers();
    } catch (error) {
      console.error("Failed to delete customer:", error);
      toast.error("Failed to delete customer");
    } finally {
      setDeletingCustomer(false);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Split into active (manually activated, or has quote, job, or subscription) vs pending (just signed up) vs not interested
  const activeCustomers = filteredCustomers.filter(c =>
    c.customer_status !== "not_interested" && (c.customer_status === "active" || c.subscription_plan !== "none" || c.latest_quote || c.total_cuts > 0 || (c.completed_jobs && c.completed_jobs.length > 0))
  );
  const pendingCustomers = filteredCustomers.filter(c =>
    c.customer_status !== "not_interested" && c.customer_status !== "active" && c.subscription_plan === "none" && !c.latest_quote && (c.total_cuts || 0) === 0 && (!c.completed_jobs || c.completed_jobs.length === 0)
  );
  const notInterestedCustomers = filteredCustomers.filter(c => c.customer_status === "not_interested");
  const displayedCustomers = customerTab === "active" ? activeCustomers : customerTab === "pending" ? pendingCustomers : notInterestedCustomers;

  const getSubscriptionBadge = (plan) => {
    if (plan === "monthly") {
      return <span className="px-2 py-1 bg-[#064E3B] text-[#39FF14] text-xs rounded-full">Monthly -10%</span>;
    } else if (plan === "yearly") {
      return <span className="px-2 py-1 bg-[#39FF14] text-black text-xs rounded-full font-bold">Yearly -25%</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">Customers</h1>
                <p className="text-[#A1A1AA] text-sm">{customers.length} total customers</p>
              </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingCustomer(null);
                setFormData({ name: "", email: "", phone: "", address: "", notes: "", one_time_price: "" });
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-customer-btn"
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all font-bold rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Customer
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase">
                    {editingCustomer ? "Edit Customer" : "Add New Customer"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Name</Label>
                    <Input
                      data-testid="customer-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Email</Label>
                    <Input
                      data-testid="customer-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Phone</Label>
                    <Input
                      data-testid="customer-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Address</Label>
                    <Textarea
                      data-testid="customer-address-input"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                    />
                    <AddressMatchSuggestion
                      address={formData.address}
                      excludeId={editingCustomer?.customer_id}
                      confirmLabel="Open this customer"
                      onConfirm={(c) => {
                        const existing = customers.find((x) => x.customer_id === c.customer_id);
                        if (existing) {
                          handleEdit(existing);
                        } else {
                          handleEdit({ ...c, notes: "" });
                        }
                        toast.info(`Editing existing customer: ${c.name}`);
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">One-Time Cut Price (optional)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39FF14] font-bold">$</span>
                      <Input
                        data-testid="customer-onetime-price-input"
                        type="number"
                        step="0.01"
                        placeholder="e.g. 45.00"
                        value={formData.one_time_price}
                        onChange={(e) => setFormData({ ...formData, one_time_price: e.target.value })}
                        className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] pl-7"
                      />
                    </div>
                    <p className="text-[#52525B] text-xs mt-1">Enter a price to create their quote right away.</p>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Notes (optional)</Label>
                    <Textarea
                      data-testid="customer-notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                    />
                  </div>
                  <Button 
                    type="submit"
                    data-testid="save-customer-btn"
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                  >
                    {editingCustomer ? "Update Customer" : "Add Customer"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
            <Input
              data-testid="search-customers-input"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14] max-w-md"
            />
          </div>

          {/* Active / Pending Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              data-testid="tab-active-customers"
              onClick={() => setCustomerTab("active")}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                customerTab === "active"
                  ? "bg-[#39FF14] text-black"
                  : "bg-[#121212] border border-[#27272A] text-[#A1A1AA] hover:text-white"
              }`}
            >
              Customers ({activeCustomers.length})
            </button>
            <button
              data-testid="tab-pending-customers"
              onClick={() => setCustomerTab("pending")}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                customerTab === "pending"
                  ? "bg-yellow-400 text-black"
                  : "bg-[#121212] border border-[#27272A] text-[#A1A1AA] hover:text-white"
              }`}
            >
              Pending ({pendingCustomers.length})
            </button>
            <button
              data-testid="tab-not-interested-customers"
              onClick={() => setCustomerTab("not_interested")}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                customerTab === "not_interested"
                  ? "bg-red-500 text-white"
                  : "bg-[#121212] border border-[#27272A] text-[#A1A1AA] hover:text-white"
              }`}
            >
              Not Interested ({notInterestedCustomers.length})
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : displayedCustomers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">
                {customerTab === "active" ? "No active customers" : customerTab === "pending" ? "No pending customers" : "No customers marked as not interested"}
              </h3>
              <p className="text-[#A1A1AA] mb-4">
                {searchQuery ? "Try a different search term" : customerTab === "active" ? "Customers with quotes, jobs, or subscriptions will appear here" : customerTab === "pending" ? "New signups without activity will appear here" : "Customers you mark as not interested will appear here"}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {displayedCustomers.map((customer) => {
                const quote = customer.latest_quote;
                const hasUnread = unreadMessages[customer.customer_id] > 0;
                const yardSizeLabels = { small: "Small (< 100 m²)", medium: "Medium (100-300 m²)", large: "Large (300-600 m²)", xlarge: "Extra Large (600+ m²)", unknown: "Unknown", "extra-large": "Extra Large (600+ m²)" };
                const grassHeightLabels = { short: "Short (< 10cm)", medium: "Medium (10-20cm)", tall: "Tall (20-30cm)", overgrown: "Overgrown (30cm+)", unknown: "Unknown" };
                
                return (
                  <div
                    key={customer.customer_id}
                    data-testid={`customer-card-${customer.customer_id}`}
                    className={`bg-[#121212] rounded-xl p-6 transition-all duration-300 ${
                      customer.subscription_plan !== "none"
                        ? "border-2 border-[#39FF14]/30 hover:border-[#39FF14]/50"
                        : "border border-[#27272A] hover:border-[#39FF14]/30"
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      {/* Customer Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#064E3B] rounded-full flex items-center justify-center relative">
                              <span className="text-[#39FF14] font-bold text-lg">
                                {customer.name.charAt(0).toUpperCase()}
                              </span>
                              {hasUnread && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                                  {unreadMessages[customer.customer_id]}
                                </span>
                              )}
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">{customer.name}</h3>
                              <div className="flex flex-wrap gap-3 mt-1 text-[#A1A1AA] text-sm">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" />
                                  {customer.email}
                                </span>
                                {customer.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-4 h-4" />
                                    {customer.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {subscriptionsEnabled && getSubscriptionBadge(customer.subscription_plan)}
                        </div>

                        {/* Quoted Prices Section - Only show if quote exists */}
                        {quote && (
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[#A1A1AA] text-xs uppercase font-semibold">QUOTED PRICES</p>
                              <Button
                                data-testid={`edit-prices-${customer.customer_id}`}
                                onClick={() => handleEditPrices(customer, quote)}
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[#A1A1AA] hover:text-[#39FF14] hover:bg-[#39FF14]/10"
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Edit All
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                              {/* Inline editable price boxes */}
                              {[
                                { key: "quoted_price", label: "One-Time Cut", color: "text-[#39FF14]", border: "border-[#27272A]", suffix: "" },
                                { key: "monthly_1x_price", label: "Monthly (1x)", color: "text-blue-400", border: "border-blue-400/30", suffix: "/mo" },
                                { key: "monthly_2x_price", label: "Monthly (2x)", color: "text-blue-300", border: "border-blue-400/30", suffix: "/mo" },
                                { key: "yearly_1x_price", label: "Yearly (1x)", color: "text-yellow-400", border: "border-yellow-400/30", suffix: "/yr" },
                                { key: "yearly_2x_price", label: "Yearly (2x)", color: "text-yellow-300", border: "border-yellow-400/30", suffix: "/yr" },
                              ].map(({ key, label, color, border, suffix }) => (
                                <div key={key} className={`p-3 bg-[#0A0A0A] rounded-lg border ${border} group`}>
                                  <p className="text-[#A1A1AA] text-xs mb-1">{label}</p>
                                  <div className="flex items-baseline gap-0.5">
                                    <span className={`${color} text-sm`}>$</span>
                                    <input
                                      data-testid={`inline-price-${customer.customer_id}-${key}`}
                                      type="number"
                                      step="0.01"
                                      defaultValue={quote[key] || ""}
                                      placeholder="0"
                                      onBlur={async (e) => {
                                        const val = parseFloat(e.target.value);
                                        if (isNaN(val) && !e.target.value) return;
                                        try {
                                          await axios.put(`${API}/quotes/${quote.quote_id}`, { [key]: val || 0 }, { withCredentials: true });
                                          toast.success(`${label} price updated`);
                                          fetchData();
                                        } catch { toast.error("Failed to update price"); }
                                      }}
                                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                      className={`${color} font-bold text-lg bg-transparent outline-none border-b border-transparent focus:border-[#39FF14]/50 w-16 appearance-none`}
                                      style={{ MozAppearance: "textfield" }}
                                    />
                                    {suffix && <span className="text-xs text-[#A1A1AA] font-normal">{suffix}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Yard Details & Address */}
                        <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-[#0A0A0A] rounded-lg">
                          <div>
                            <p className="text-[#A1A1AA] text-xs uppercase mb-1">Yard Size</p>
                            <p className="text-white font-medium">{quote?.yard_size ? yardSizeLabels[quote.yard_size] || quote.yard_size : "Not specified"}</p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA] text-xs uppercase mb-1">Grass Height</p>
                            <p className="text-white font-medium">{quote?.grass_height ? grassHeightLabels[quote.grass_height] || quote.grass_height : "Not specified"}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-[#A1A1AA] text-xs uppercase mb-1">Address</p>
                            <p className="text-white font-medium">{quote?.customer_address || customer.address || "No address on file"}</p>
                          </div>
                        </div>

                        {/* Subscription Control Section */}
                        {subscriptionsEnabled && <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[#A1A1AA] text-xs uppercase font-semibold flex items-center gap-2">
                              <CreditCard className="w-4 h-4" />
                              Subscription
                            </p>
                            {customer.subscription_plan !== "none" && (
                              <span className="text-xs text-[#39FF14] font-medium">
                                Active: {customer.subscription_plan === "yearly" ? "Yearly" : "Monthly"} {customer.subscription_frequency || 1}x/mo
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-6 gap-2">
                            {/* 1-Time Cut Price */}
                            <div className="p-2.5 rounded-lg border-2 border-[#27272A] bg-[#0A0A0A] text-center">
                              <p className="text-[10px] font-bold leading-tight text-[#A1A1AA]">1-TIME</p>
                              <div className="flex items-center justify-center mt-1">
                                <span className="text-[#A1A1AA] text-xs">$</span>
                                <input
                                  data-testid={`one-time-price-${customer.customer_id}`}
                                  type="number"
                                  defaultValue={quote?.quoted_price || ""}
                                  placeholder="0"
                                  onBlur={async (e) => {
                                    const price = parseFloat(e.target.value);
                                    if (!price || isNaN(price)) return;
                                    try {
                                      if (quote?.quote_id) {
                                        await axios.put(`${API}/quotes/${quote.quote_id}`, { quoted_price: price }, { withCredentials: true });
                                      } else {
                                        const custId = customer.customer_id || customer.user_id || "";
                                        await axios.post(`${API}/admin/quotes/create`, {
                                          customer_id: custId,
                                          customer_name: customer.name,
                                          customer_email: customer.email || "",
                                          customer_phone: customer.phone || "",
                                          customer_address: customer.address || "",
                                          quoted_price: price,
                                          yard_size: "medium",
                                          grass_height: "medium",
                                        }, { withCredentials: true });
                                      }
                                      toast.success("1-time price saved");
                                      fetchData();
                                    } catch (err) {
                                      console.error("Save price error:", err?.response?.data);
                                      toast.error(err?.response?.data?.detail || "Failed to save price");
                                    }
                                  }}
                                  className="w-12 bg-transparent text-white text-sm font-bold text-center outline-none border-b border-[#39FF14]/30 focus:border-[#39FF14]"
                                />
                              </div>
                            </div>
                            <button
                              data-testid={`sub-monthly-1x-${customer.customer_id}`}
                              onClick={async () => {
                                if (customer.subscription_plan === "monthly" && customer.subscription_frequency === 1) {
                                  try {
                                    await axios.delete(`${API}/subscriptions/${customer.customer_id}`, { withCredentials: true });
                                    toast.success("Subscription deactivated");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to deactivate"); }
                                } else {
                                  try {
                                    await axios.post(`${API}/subscriptions`, { customer_id: customer.customer_id, plan: "monthly", frequency: 1 }, { withCredentials: true });
                                    toast.success("Monthly 1x subscription activated!");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate subscription"); }
                                }
                              }}
                              className={`p-2.5 rounded-lg border-2 transition-all text-center cursor-pointer ${
                                customer.subscription_plan === "monthly" && customer.subscription_frequency === 1
                                  ? "bg-blue-400/15 border-blue-400 text-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.2)]"
                                  : "bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-blue-400/50 hover:bg-blue-400/5"
                              }`}
                            >
                              <p className="text-[10px] font-bold leading-tight">MONTHLY</p>
                              <p className="text-[10px]">1x/mo</p>
                              {customer.subscription_plan === "monthly" && customer.subscription_frequency === 1 && (
                                <p className="text-[9px] mt-0.5 font-medium">ACTIVE</p>
                              )}
                            </button>
                            <button
                              data-testid={`sub-monthly-2x-${customer.customer_id}`}
                              onClick={async () => {
                                if (customer.subscription_plan === "monthly" && customer.subscription_frequency === 2) {
                                  try {
                                    await axios.delete(`${API}/subscriptions/${customer.customer_id}`, { withCredentials: true });
                                    toast.success("Subscription deactivated");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to deactivate"); }
                                } else {
                                  try {
                                    await axios.post(`${API}/subscriptions`, { customer_id: customer.customer_id, plan: "monthly", frequency: 2 }, { withCredentials: true });
                                    toast.success("Monthly 2x subscription activated!");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate subscription"); }
                                }
                              }}
                              className={`p-2.5 rounded-lg border-2 transition-all text-center cursor-pointer ${
                                customer.subscription_plan === "monthly" && customer.subscription_frequency === 2
                                  ? "bg-blue-300/15 border-blue-300 text-blue-300 shadow-[0_0_8px_rgba(147,197,253,0.2)]"
                                  : "bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-blue-300/50 hover:bg-blue-300/5"
                              }`}
                            >
                              <p className="text-[10px] font-bold leading-tight">MONTHLY</p>
                              <p className="text-[10px]">2x/mo</p>
                              {customer.subscription_plan === "monthly" && customer.subscription_frequency === 2 && (
                                <p className="text-[9px] mt-0.5 font-medium">ACTIVE</p>
                              )}
                            </button>
                            <button
                              data-testid={`sub-yearly-1x-${customer.customer_id}`}
                              onClick={async () => {
                                if (customer.subscription_plan === "yearly" && customer.subscription_frequency === 1) {
                                  try {
                                    await axios.delete(`${API}/subscriptions/${customer.customer_id}`, { withCredentials: true });
                                    toast.success("Subscription deactivated");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to deactivate"); }
                                } else {
                                  try {
                                    await axios.post(`${API}/subscriptions`, { customer_id: customer.customer_id, plan: "yearly", frequency: 1 }, { withCredentials: true });
                                    toast.success("Yearly 1x subscription activated!");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate subscription"); }
                                }
                              }}
                              className={`p-2.5 rounded-lg border-2 transition-all text-center cursor-pointer ${
                                customer.subscription_plan === "yearly" && customer.subscription_frequency === 1
                                  ? "bg-yellow-400/15 border-yellow-400 text-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.2)]"
                                  : "bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-yellow-400/50 hover:bg-yellow-400/5"
                              }`}
                            >
                              <p className="text-[10px] font-bold leading-tight">YEARLY</p>
                              <p className="text-[10px]">1x/mo</p>
                              {customer.subscription_plan === "yearly" && customer.subscription_frequency === 1 && (
                                <p className="text-[9px] mt-0.5 font-medium">ACTIVE</p>
                              )}
                            </button>
                            <button
                              data-testid={`sub-yearly-2x-${customer.customer_id}`}
                              onClick={async () => {
                                if (customer.subscription_plan === "yearly" && customer.subscription_frequency === 2) {
                                  try {
                                    await axios.delete(`${API}/subscriptions/${customer.customer_id}`, { withCredentials: true });
                                    toast.success("Subscription deactivated");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to deactivate"); }
                                } else {
                                  try {
                                    await axios.post(`${API}/subscriptions`, { customer_id: customer.customer_id, plan: "yearly", frequency: 2 }, { withCredentials: true });
                                    toast.success("Yearly 2x subscription activated!");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate subscription"); }
                                }
                              }}
                              className={`p-2.5 rounded-lg border-2 transition-all text-center cursor-pointer ${
                                customer.subscription_plan === "yearly" && customer.subscription_frequency === 2
                                  ? "bg-yellow-300/15 border-yellow-300 text-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.2)]"
                                  : "bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-yellow-300/50 hover:bg-yellow-300/5"
                              }`}
                            >
                              <p className="text-[10px] font-bold leading-tight">YEARLY</p>
                              <p className="text-[10px]">2x/mo</p>
                              {customer.subscription_plan === "yearly" && customer.subscription_frequency === 2 && (
                                <p className="text-[9px] mt-0.5 font-medium">ACTIVE</p>
                              )}
                            </button>
                            <button
                              data-testid={`sub-none-${customer.customer_id}`}
                              onClick={async () => {
                                if (customer.subscription_plan !== "none") {
                                  try {
                                    await axios.delete(`${API}/subscriptions/${customer.customer_id}`, { withCredentials: true });
                                    toast.success("Subscription deactivated");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Failed to deactivate"); }
                                }
                              }}
                              className={`p-2.5 rounded-lg border-2 transition-all text-center cursor-pointer ${
                                customer.subscription_plan === "none"
                                  ? "bg-[#27272A]/50 border-[#A1A1AA]/30 text-[#A1A1AA]"
                                  : "bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-red-400/50 hover:bg-red-400/5"
                              }`}
                            >
                              <p className="text-[10px] font-bold leading-tight">NONE</p>
                              {customer.subscription_plan === "none" && (
                                <p className="text-[9px] mt-0.5 font-medium">CURRENT</p>
                              )}
                            </button>
                          </div>
                        </div>}

                        {/* Pending/Scheduled Cuts */}
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[#A1A1AA] text-xs uppercase font-semibold flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Pending Cuts ({customer.pending_count || 0})
                            </p>
                          </div>
                          {customer.pending_jobs && customer.pending_jobs.length > 0 ? (
                            <div className="bg-[#0A0A0A] rounded-lg border border-yellow-500/30 overflow-hidden">
                              <div className="max-h-32 overflow-y-auto">
                                {customer.pending_jobs.map((job, idx) => (
                                  <div 
                                    key={job.job_id || idx}
                                    className={`flex items-center justify-between px-4 py-2.5 ${idx !== customer.pending_jobs.length - 1 ? 'border-b border-[#27272A]' : ''}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Clock className="w-4 h-4 text-yellow-400" />
                                      <span className="text-white text-sm">
                                        {job.scheduled_date 
                                          ? format(parseISO(job.scheduled_date), "MMM d, yyyy")
                                          : "Date TBD"
                                        }
                                      </span>
                                      {job.scheduled_time && (
                                        <span className="text-[#A1A1AA] text-xs">{job.scheduled_time}</span>
                                      )}
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        job.status === 'completed' ? 'bg-[#39FF14]/10 text-[#39FF14]' :
                                        job.status === 'confirmed' ? 'bg-blue-400/10 text-blue-400' : 'bg-yellow-400/10 text-yellow-400'
                                      }`}>
                                        {job.status}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {job.status !== "completed" && (
                                        <button
                                          onClick={async () => {
                                            try {
                                              await axios.put(`${API}/jobs/${job.job_id}`, { status: "completed", completed_at: new Date().toISOString() }, { withCredentials: true });
                                              toast.success("Job marked as completed!");
                                              fetchData();
                                            } catch { toast.error("Failed to complete job"); }
                                          }}
                                          className="text-xs px-2 py-1 rounded bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 hover:bg-[#39FF14]/20 transition-colors font-medium"
                                          data-testid={`complete-job-${job.job_id}`}
                                        >
                                          Complete
                                        </button>
                                      )}
                                      <button
                                        onClick={async () => {
                                          const newStatus = job.payment_status === "paid" ? "pending" : "paid";
                                          try {
                                            await axios.put(`${API}/jobs/${job.job_id}`, { payment_status: newStatus }, { withCredentials: true });
                                            toast.success(newStatus === "paid" ? "Marked as Paid" : "Marked as Unpaid");
                                            fetchData();
                                          } catch { toast.error("Failed to update payment"); }
                                        }}
                                        className={`text-xs px-2 py-1 rounded border font-bold transition-colors ${
                                          job.payment_status === "paid"
                                            ? "bg-[#39FF14]/15 border-[#39FF14]/30 text-[#39FF14]"
                                            : "bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
                                        }`}
                                        data-testid={`payment-job-${job.job_id}`}
                                      >
                                        {job.payment_status === "paid" ? "PAID" : "UNPAID"}
                                      </button>
                                      {job.price > 0 && (
                                        <span className="text-yellow-400 font-medium text-sm">${(job.price || 0).toFixed(2)}</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A] text-center">
                              <p className="text-[#A1A1AA] text-sm">No pending cuts</p>
                            </div>
                          )}
                        </div>

                        {/* Completed Cuts History */}
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[#A1A1AA] text-xs uppercase font-semibold flex items-center gap-2">
                              <Scissors className="w-4 h-4" />
                              Completed Cuts ({customer.total_cuts || 0})
                            </p>
                          </div>
                          {customer.completed_jobs && customer.completed_jobs.length > 0 ? (
                            <div className="bg-[#0A0A0A] rounded-lg border border-[#27272A] overflow-hidden">
                              <div className="max-h-32 overflow-y-auto">
                                {[...customer.completed_jobs].sort((a, b) => (b.completed_at || b.scheduled_date || "").localeCompare(a.completed_at || a.scheduled_date || "")).map((job, idx) => (
                                  <div 
                                    key={job.job_id || idx}
                                    className={`flex items-center justify-between px-4 py-2 ${idx !== customer.completed_jobs.length - 1 ? 'border-b border-[#27272A]' : ''}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <CheckCircle className="w-4 h-4 text-[#39FF14]" />
                                      <span className="text-white text-sm">
                                        {job.completed_at 
                                          ? format(parseISO(job.completed_at), "MMM d, yyyy")
                                          : job.scheduled_date 
                                            ? format(parseISO(job.scheduled_date), "MMM d, yyyy")
                                            : "Date unknown"
                                        }
                                      </span>
                                    </div>
                                    {job.price > 0 && (
                                      <span className="text-[#39FF14] font-medium text-sm">${job.price.toFixed(2)}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A] text-center">
                              <Scissors className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA] opacity-50" />
                              <p className="text-[#A1A1AA] text-sm">No completed cuts yet</p>
                            </div>
                          )}
                        </div>

                        {/* Timestamps */}
                        <div className="flex items-center gap-4 mt-4">
                          {customer.created_at && (
                            <span className="text-[#A1A1AA] text-xs">
                              Customer since {format(parseISO(customer.created_at), "MMM d, yyyy")}
                            </span>
                          )}
                          {quote?.sent_at && (
                            <span className="text-[#A1A1AA] text-xs">
                              • Quote sent {format(parseISO(quote.sent_at), "MMM d, yyyy")}
                            </span>
                          )}
                        </div>

                        {/* Activity Log */}
                        {!activityLogs[customer.customer_id] ? (
                          <button
                            onClick={() => fetchActivityLog(customer.customer_id)}
                            className="text-[#39FF14] text-xs mt-2 hover:underline"
                            data-testid={`load-log-${customer.customer_id}`}
                          >
                            View activity log
                          </button>
                        ) : (
                          <div className="mt-3 border-t border-[#27272A] pt-3">
                            <p className="text-[#A1A1AA] text-xs font-bold uppercase tracking-wider mb-2">Activity Log</p>
                            {activityLogs[customer.customer_id].length === 0 ? (
                              <p className="text-[#52525B] text-xs">No activity yet</p>
                            ) : (
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {activityLogs[customer.customer_id].map((log, i) => {
                                  const logKey = `${customer.customer_id}-${i}`;
                                  const isQuote = log.type === "quote_sent";
                                  const isMessage = log.type === "message";
                                  const isCut = log.type === "cut_completed";
                                  const isOpen = expandedLog === logKey;
                                  if (isCut) {
                                    return (
                                      <div key={i} className="text-xs py-1" data-testid={`activity-cut-${customer.customer_id}-${i}`}>
                                        <div className="flex items-start gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 bg-[#FACC15]" />
                                          <span className="text-[#A1A1AA] flex-1 min-w-0">
                                            Cut completed{log.staff_name ? ` by ${log.staff_name}` : ""}
                                            {log.price ? ` — $${Number(log.price).toFixed(2)}` : ""}
                                            {log.payment_status ? ` (${log.payment_status})` : ""}
                                          </span>
                                          <span className="text-[#52525B] ml-auto shrink-0">
                                            {log.created_at ? format(parseISO(log.created_at), "MMM d, yyyy") : ""}
                                          </span>
                                        </div>
                                        <PhotoThumbs photos={log.photos} testIdPrefix={`activity-cut-photo-${customer.customer_id}-${i}`} sizeClass="w-12 h-12" className="ml-3.5 mt-1" />
                                      </div>
                                    );
                                  }
                                  return (
                                  <div key={i} className={isQuote ? "rounded-md hover:bg-[#39FF14]/5" : ""}>
                                    <div
                                      className={`flex items-start gap-2 text-xs ${isQuote ? "cursor-pointer py-1" : ""}`}
                                      data-testid={isQuote ? `activity-quote-${customer.customer_id}-${i}` : (isMessage ? `activity-message-${customer.customer_id}-${i}` : undefined)}
                                      onClick={isQuote ? () => setExpandedLog(isOpen ? null : logKey) : undefined}
                                    >
                                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${log.type === "email_sent" ? "bg-[#1877F2]" : isMessage ? "bg-[#FACC15]" : "bg-[#39FF14]"}`} />
                                      <span className="text-[#A1A1AA] flex-1 min-w-0">
                                        {isMessage ? (
                                          <>
                                            <span className={log.sender_type === "customer" ? "text-[#38BDF8] font-semibold" : "text-[#39FF14] font-semibold"}>
                                              {log.sender_type === "customer" ? (log.sender_name || "Customer") : "You"}:
                                            </span>{" "}
                                            <span className="text-white">{log.message}</span>
                                          </>
                                        ) : (
                                          <>
                                            {log.type === "email_sent" ? "Email sent" : "Quote sent"}
                                            {log.subject ? ` — "${log.subject}"` : ""}
                                            {log.price ? ` — $${Number(log.price).toFixed(2)}` : ""}
                                          </>
                                        )}
                                      </span>
                                      {isQuote && <span className="text-[#39FF14] text-[10px] shrink-0">{isOpen ? "▲ hide" : "▼ view"}</span>}
                                      <span className="text-[#52525B] ml-auto shrink-0">
                                        {log.created_at ? format(parseISO(log.created_at), "MMM d, yyyy") : ""}
                                      </span>
                                    </div>
                                    {isQuote && isOpen && (
                                      <div className="ml-3.5 mb-2 mt-1 bg-[#0A0A0A] border border-[#27272A] rounded-md p-2.5 space-y-1" data-testid={`activity-quote-detail-${customer.customer_id}-${i}`}>
                                        <p className="text-[#A1A1AA] text-[10px] uppercase tracking-wider font-bold">What was sent</p>
                                        {log.customer_address && <p className="text-white text-xs"><span className="text-[#52525B]">Address:</span> {log.customer_address}</p>}
                                        {(log.yard_size || log.grass_height) && <p className="text-white text-xs"><span className="text-[#52525B]">Yard:</span> {log.yard_size || "—"} · <span className="text-[#52525B]">Grass:</span> {log.grass_height || "—"}</p>}
                                        {log.price != null && <p className="text-[#39FF14] text-xs">One-time: ${Number(log.price).toFixed(2)}</p>}
                                        {log.monthly_1x_price != null && <p className="text-white text-xs">Monthly 1x: ${Number(log.monthly_1x_price).toFixed(2)}</p>}
                                        {log.monthly_2x_price != null && <p className="text-white text-xs">Monthly 2x: ${Number(log.monthly_2x_price).toFixed(2)}</p>}
                                        {log.yearly_1x_price != null && <p className="text-white text-xs">Yearly 1x: ${Number(log.yearly_1x_price).toFixed(2)}</p>}
                                        {log.yearly_2x_price != null && <p className="text-white text-xs">Yearly 2x: ${Number(log.yearly_2x_price).toFixed(2)}</p>}
                                        {log.notes && <p className="text-white text-xs pt-1 border-t border-[#27272A]"><span className="text-[#52525B]">Message:</span> {log.notes}</p>}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-row lg:flex-col gap-2 flex-wrap">
                        {/* Activate Button (pending tab only) */}
                        {customerTab === "pending" && (
                          <>
                            <Button
                              data-testid={`activate-customer-${customer.customer_id}`}
                              onClick={() => handleActivateCustomer(customer)}
                              className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate
                            </Button>
                            <Button
                              data-testid={`not-interested-${customer.customer_id}`}
                              onClick={() => handleSetCustomerStatus(customer.customer_id, "not_interested")}
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Not Interested
                            </Button>
                          </>
                        )}
                        {/* Reactivate Button (not interested tab only) */}
                        {customerTab === "not_interested" && (
                          <Button
                            data-testid={`reactivate-customer-${customer.customer_id}`}
                            onClick={() => handleSetCustomerStatus(customer.customer_id, "active")}
                            className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Reactivate
                          </Button>
                        )}
                        {/* Chat Button */}
                        <Button
                          data-testid={`chat-customer-${customer.customer_id}`}
                          onClick={() => openQuickChat(customer)}
                          variant="outline"
                          className={`border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10 ${hasUnread ? 'animate-pulse' : ''}`}
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Chat {hasUnread && `(${unreadMessages[customer.customer_id]})`}
                        </Button>

                        {/* Email Message Button */}
                        {customer.email && (
                          <Button
                            variant="outline"
                            className="border-[#1877F2]/50 text-[#1877F2] hover:bg-[#1877F2]/10"
                            data-testid={`email-customer-${customer.customer_id}`}
                            onClick={() => openEmailDialog(customer)}
                          >
                            <MailIcon className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                        )}

                        {/* Yearly Receipt Button */}
                        <Button
                          data-testid={`receipt-customer-${customer.customer_id}`}
                          onClick={() => handleGenerateReceipt(customer)}
                          variant="outline"
                          className="border-[#A1A1AA]/30 text-[#A1A1AA] hover:bg-[#A1A1AA]/10 hover:text-white"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Receipt
                        </Button>

                        {/* Quote Status or Send Quote */}
                        {quote?.status === "sent" ? (
                          <Button
                            variant="outline"
                            className="border-blue-400/50 text-blue-400 hover:bg-blue-400/10"
                            onClick={() => handleViewCustomer(customer)}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Quote Sent
                          </Button>
                        ) : (
                          <Button
                            className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold w-full"
                            onClick={() => handleOpenCreateQuote(customer)}
                            data-testid={`send-quote-${customer.customer_id}`}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Send Quote
                          </Button>
                        )}

                        {/* Mark as Paid */}
                        {quote?.quoted_price && customer.subscription_plan === "none" && (
                          <Button
                            className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                            onClick={() => toast.info("Payment tracking coming soon")}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark as Paid
                          </Button>
                        )}

                        {/* Schedule Job */}
                        <Link to={`/schedule?customer=${customer.customer_id}`}>
                          <Button
                            variant="outline"
                            className="border-purple-400/50 text-purple-400 hover:bg-purple-400/10 w-full"
                          >
                            <CalendarPlus className="w-4 h-4 mr-2" />
                            Schedule Job
                          </Button>
                        </Link>

                        {/* Edit, View, Delete, Print */}
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-customer-${customer.customer_id}`}
                            onClick={() => handleEdit(customer)}
                            variant="outline"
                            size="sm"
                            className="border-[#27272A] text-[#A1A1AA] hover:text-white flex-1"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`view-customer-${customer.customer_id}`}
                            onClick={() => handleViewCustomer(customer)}
                            variant="outline"
                            size="sm"
                            className="border-[#27272A] text-[#A1A1AA] hover:text-white flex-1"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`print-customer-${customer.customer_id}`}
                            onClick={() => handlePrintCustomer(customer, quote)}
                            variant="outline"
                            size="sm"
                            className="border-[#27272A] text-[#A1A1AA] hover:text-white flex-1"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`invite-customer-${customer.customer_id}`}
                            onClick={() => handleCopyInvite(customer)}
                            variant="outline"
                            size="sm"
                            className="border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 flex-1"
                            title="Copy invite link"
                          >
                            <Link2 className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-customer-${customer.customer_id}`}
                            onClick={() => handleDelete(customer.customer_id)}
                            variant="outline"
                            size="sm"
                            className="border-red-400/50 text-red-400 hover:bg-red-400/10 flex-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Customer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-[#A1A1AA]">
              Are you sure you want to delete this customer? This action cannot be undone and will remove all associated data.
            </p>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setCustomerToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteCustomer}
                disabled={deletingCustomer}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold"
              >
                {deletingCustomer ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => {
        setViewDialogOpen(open);
        if (!open) {
          setEditingQuote(null);
          setEditingJob(null);
          setCreateQuoteOpen(false);
        }
      }}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-3">
              <div className="w-10 h-10 bg-[#064E3B] rounded-full flex items-center justify-center">
                <span className="text-[#39FF14] font-bold">
                  {selectedCustomer?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <span>{selectedCustomer?.name}</span>
                <p className="text-sm text-[#A1A1AA] font-normal">{selectedCustomer?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <Tabs defaultValue="quotes" className="mt-4">
              <TabsList className="bg-[#0A0A0A] border border-[#27272A]">
                <TabsTrigger value="quotes" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                  Quotes ({customerQuotes.length})
                </TabsTrigger>
                <TabsTrigger value="appointments" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                  Appointments ({customerJobs.length})
                </TabsTrigger>
                <TabsTrigger value="chat" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                  Chat ({messages.length})
                </TabsTrigger>
                <TabsTrigger value="stats" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                  Stats
                </TabsTrigger>
                <TabsTrigger value="info" className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black">
                  Info
                </TabsTrigger>
              </TabsList>

              {/* Quotes Tab */}
              <TabsContent value="quotes" className="mt-4 space-y-3">
                {/* Create Quote Button */}
                <Button
                  data-testid="create-quote-btn"
                  onClick={() => setCreateQuoteOpen(!createQuoteOpen)}
                  className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Quote for {selectedCustomer?.name?.split(" ")[0]}
                </Button>

                {/* Create Quote Form */}
                {createQuoteOpen && (
                  <div className="bg-[#0A0A0A] border border-[#39FF14]/30 rounded-lg p-4 space-y-3" data-testid="create-quote-form">
                    <h4 className="text-sm font-bold text-[#39FF14] uppercase">New Quote</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Yard Size</Label>
                        <Select value={newQuoteData.yard_size} onValueChange={(v) => setNewQuoteData({ ...newQuoteData, yard_size: v })}>
                          <SelectTrigger className="h-8 bg-[#121212] border-[#27272A] text-white text-xs" data-testid="new-quote-yard-size">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-[#27272A]">
                            <SelectItem value="small" className="text-white text-xs">Small</SelectItem>
                            <SelectItem value="medium" className="text-white text-xs">Medium</SelectItem>
                            <SelectItem value="large" className="text-white text-xs">Large</SelectItem>
                            <SelectItem value="extra-large" className="text-white text-xs">Extra Large</SelectItem>
                            <SelectItem value="unknown" className="text-white text-xs">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Grass Height</Label>
                        <Select value={newQuoteData.grass_height} onValueChange={(v) => setNewQuoteData({ ...newQuoteData, grass_height: v })}>
                          <SelectTrigger className="h-8 bg-[#121212] border-[#27272A] text-white text-xs" data-testid="new-quote-grass-height">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-[#27272A]">
                            <SelectItem value="short" className="text-white text-xs">Short</SelectItem>
                            <SelectItem value="medium" className="text-white text-xs">Medium</SelectItem>
                            <SelectItem value="tall" className="text-white text-xs">Tall</SelectItem>
                            <SelectItem value="overgrown" className="text-white text-xs">Overgrown</SelectItem>
                            <SelectItem value="unknown" className="text-white text-xs">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Address</Label>
                      <Input
                        placeholder="Customer's property address"
                        value={newQuoteData.customer_address}
                        onChange={(e) => setNewQuoteData({ ...newQuoteData, customer_address: e.target.value })}
                        className="h-8 bg-[#121212] border-[#27272A] text-white text-xs"
                        data-testid="new-quote-address"
                      />
                    </div>
                    {/* Pricing */}
                    <div className="space-y-2">
                      <Label className="text-xs text-[#A1A1AA] font-semibold uppercase">One-Time Cut</Label>
                      <div className="flex gap-1">
                        <Input type={newQuoteData.quoted_price === "N/A" ? "text" : "number"} placeholder="0.00" value={newQuoteData.quoted_price} onChange={(e) => setNewQuoteData({ ...newQuoteData, quoted_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={newQuoteData.quoted_price === "N/A"} data-testid="new-quote-price" />
                        <Button type="button" size="sm" variant={newQuoteData.quoted_price === "N/A" ? "default" : "ghost"} className={`h-8 px-2 text-xs ${newQuoteData.quoted_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setNewQuoteData({ ...newQuoteData, quoted_price: newQuoteData.quoted_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                      </div>
                    </div>
                    <div className="space-y-2 pt-2 border-t border-[#27272A]">
                      <Label className="text-xs text-[#A1A1AA] font-semibold uppercase">Monthly Plan</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">1 Cut/Month</Label>
                          <div className="flex gap-1">
                            <Input type={newQuoteData.monthly_1x_price === "N/A" ? "text" : "number"} placeholder="0.00" value={newQuoteData.monthly_1x_price} onChange={(e) => setNewQuoteData({ ...newQuoteData, monthly_1x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={newQuoteData.monthly_1x_price === "N/A"} />
                            <Button type="button" size="sm" variant={newQuoteData.monthly_1x_price === "N/A" ? "default" : "ghost"} className={`h-8 px-2 text-xs ${newQuoteData.monthly_1x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setNewQuoteData({ ...newQuoteData, monthly_1x_price: newQuoteData.monthly_1x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">2 Cuts/Month</Label>
                          <div className="flex gap-1">
                            <Input type={newQuoteData.monthly_2x_price === "N/A" ? "text" : "number"} placeholder="0.00" value={newQuoteData.monthly_2x_price} onChange={(e) => setNewQuoteData({ ...newQuoteData, monthly_2x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={newQuoteData.monthly_2x_price === "N/A"} />
                            <Button type="button" size="sm" variant={newQuoteData.monthly_2x_price === "N/A" ? "default" : "ghost"} className={`h-8 px-2 text-xs ${newQuoteData.monthly_2x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setNewQuoteData({ ...newQuoteData, monthly_2x_price: newQuoteData.monthly_2x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 pt-2 border-t border-[#27272A]">
                      <Label className="text-xs text-[#A1A1AA] font-semibold uppercase">Yearly Plan</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">1 Cut/Month</Label>
                          <div className="flex gap-1">
                            <Input type={newQuoteData.yearly_1x_price === "N/A" ? "text" : "number"} placeholder="0.00" value={newQuoteData.yearly_1x_price} onChange={(e) => setNewQuoteData({ ...newQuoteData, yearly_1x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={newQuoteData.yearly_1x_price === "N/A"} />
                            <Button type="button" size="sm" variant={newQuoteData.yearly_1x_price === "N/A" ? "default" : "ghost"} className={`h-8 px-2 text-xs ${newQuoteData.yearly_1x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setNewQuoteData({ ...newQuoteData, yearly_1x_price: newQuoteData.yearly_1x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">2 Cuts/Month</Label>
                          <div className="flex gap-1">
                            <Input type={newQuoteData.yearly_2x_price === "N/A" ? "text" : "number"} placeholder="0.00" value={newQuoteData.yearly_2x_price} onChange={(e) => setNewQuoteData({ ...newQuoteData, yearly_2x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={newQuoteData.yearly_2x_price === "N/A"} />
                            <Button type="button" size="sm" variant={newQuoteData.yearly_2x_price === "N/A" ? "default" : "ghost"} className={`h-8 px-2 text-xs ${newQuoteData.yearly_2x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setNewQuoteData({ ...newQuoteData, yearly_2x_price: newQuoteData.yearly_2x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Notes</Label>
                      <Textarea
                        placeholder="Any notes about this quote..."
                        value={newQuoteData.notes}
                        onChange={(e) => setNewQuoteData({ ...newQuoteData, notes: e.target.value })}
                        className="h-16 bg-[#121212] border-[#27272A] text-white text-xs"
                        data-testid="new-quote-notes"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCreateQuoteOpen(false)}
                        className="text-[#A1A1AA]"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCreateQuote}
                        disabled={creatingQuote}
                        className="bg-[#39FF14] text-black hover:bg-[#32D711] flex-1"
                        data-testid="submit-create-quote-btn"
                      >
                        {creatingQuote ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                        {newQuoteData.quoted_price ? (selectedCustomer?.email ? "Create & Send Quote" : "Save Quote") : "Create Quote"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Existing quotes list */}
                {customerQuotes.length === 0 && !createQuoteOpen ? (
                  <div className="text-center py-8 text-[#A1A1AA]">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No quotes for this customer</p>
                  </div>
                ) : (
                  customerQuotes.map((quote) => (
                    <div key={quote.quote_id} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                      {editingQuote === quote.quote_id ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-[#A1A1AA] font-mono">{quote.quote_id}</span>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditingQuote(null)}>
                                Cancel
                              </Button>
                              <Button size="sm" onClick={() => handleSaveQuote(quote.quote_id)} className="bg-[#39FF14] text-black hover:bg-[#32D711]">
                                <Save className="w-3 h-3 mr-1" /> Save
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Yard Size</Label>
                              <Select value={quoteEditData.yard_size} onValueChange={(v) => setQuoteEditData({ ...quoteEditData, yard_size: v })}>
                                <SelectTrigger className="h-8 bg-[#121212] border-[#27272A] text-white text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#27272A]">
                                  <SelectItem value="small" className="text-white text-xs">Small</SelectItem>
                                  <SelectItem value="medium" className="text-white text-xs">Medium</SelectItem>
                                  <SelectItem value="large" className="text-white text-xs">Large</SelectItem>
                                  <SelectItem value="extra-large" className="text-white text-xs">Extra Large</SelectItem>
                                  <SelectItem value="unknown" className="text-white text-xs">Unknown</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Grass Height</Label>
                              <Select value={quoteEditData.grass_height} onValueChange={(v) => setQuoteEditData({ ...quoteEditData, grass_height: v })}>
                                <SelectTrigger className="h-8 bg-[#121212] border-[#27272A] text-white text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#27272A]">
                                  <SelectItem value="short" className="text-white text-xs">Short</SelectItem>
                                  <SelectItem value="medium" className="text-white text-xs">Medium</SelectItem>
                                  <SelectItem value="tall" className="text-white text-xs">Tall</SelectItem>
                                  <SelectItem value="overgrown" className="text-white text-xs">Overgrown</SelectItem>
                                  <SelectItem value="unknown" className="text-white text-xs">Unknown</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-[#A1A1AA]">Address</Label>
                            <Input
                              value={quoteEditData.customer_address}
                              onChange={(e) => setQuoteEditData({ ...quoteEditData, customer_address: e.target.value })}
                              placeholder="Property address"
                              className="h-8 bg-[#121212] border-[#27272A] text-white text-xs"
                            />
                          </div>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">One-Time Cut</Label>
                              <div className="flex gap-1">
                                <Input
                                  type={quoteEditData.quoted_price === "N/A" ? "text" : "number"}
                                  value={quoteEditData.quoted_price}
                                  onChange={(e) => setQuoteEditData({ ...quoteEditData, quoted_price: e.target.value })}
                                  className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1"
                                  disabled={quoteEditData.quoted_price === "N/A"}
                                  placeholder="0.00"
                                />
                                <Button
                                  type="button" size="sm"
                                  className={`h-8 px-2 text-xs ${quoteEditData.quoted_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`}
                                  variant={quoteEditData.quoted_price === "N/A" ? "default" : "ghost"}
                                  onClick={() => setQuoteEditData({ ...quoteEditData, quoted_price: quoteEditData.quoted_price === "N/A" ? "" : "N/A" })}
                                >N/A</Button>
                              </div>
                            </div>
                            <div className="border-t border-[#27272A] pt-2">
                              <Label className="text-xs text-[#A1A1AA] font-semibold uppercase">Monthly Plan</Label>
                              <div className="grid grid-cols-2 gap-2 mt-1">
                                <div>
                                  <Label className="text-xs text-[#A1A1AA]">1x/mo</Label>
                                  <div className="flex gap-1">
                                    <Input type={quoteEditData.monthly_1x_price === "N/A" ? "text" : "number"} value={quoteEditData.monthly_1x_price} onChange={(e) => setQuoteEditData({ ...quoteEditData, monthly_1x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={quoteEditData.monthly_1x_price === "N/A"} placeholder="0.00" />
                                    <Button type="button" size="sm" className={`h-8 px-2 text-xs ${quoteEditData.monthly_1x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} variant={quoteEditData.monthly_1x_price === "N/A" ? "default" : "ghost"} onClick={() => setQuoteEditData({ ...quoteEditData, monthly_1x_price: quoteEditData.monthly_1x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs text-[#A1A1AA]">2x/mo</Label>
                                  <div className="flex gap-1">
                                    <Input type={quoteEditData.monthly_2x_price === "N/A" ? "text" : "number"} value={quoteEditData.monthly_2x_price} onChange={(e) => setQuoteEditData({ ...quoteEditData, monthly_2x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={quoteEditData.monthly_2x_price === "N/A"} placeholder="0.00" />
                                    <Button type="button" size="sm" className={`h-8 px-2 text-xs ${quoteEditData.monthly_2x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} variant={quoteEditData.monthly_2x_price === "N/A" ? "default" : "ghost"} onClick={() => setQuoteEditData({ ...quoteEditData, monthly_2x_price: quoteEditData.monthly_2x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="border-t border-[#27272A] pt-2">
                              <Label className="text-xs text-[#A1A1AA] font-semibold uppercase">Yearly Plan</Label>
                              <div className="grid grid-cols-2 gap-2 mt-1">
                                <div>
                                  <Label className="text-xs text-[#A1A1AA]">1x/mo</Label>
                                  <div className="flex gap-1">
                                    <Input type={quoteEditData.yearly_1x_price === "N/A" ? "text" : "number"} value={quoteEditData.yearly_1x_price} onChange={(e) => setQuoteEditData({ ...quoteEditData, yearly_1x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={quoteEditData.yearly_1x_price === "N/A"} placeholder="0.00" />
                                    <Button type="button" size="sm" className={`h-8 px-2 text-xs ${quoteEditData.yearly_1x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} variant={quoteEditData.yearly_1x_price === "N/A" ? "default" : "ghost"} onClick={() => setQuoteEditData({ ...quoteEditData, yearly_1x_price: quoteEditData.yearly_1x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs text-[#A1A1AA]">2x/mo</Label>
                                  <div className="flex gap-1">
                                    <Input type={quoteEditData.yearly_2x_price === "N/A" ? "text" : "number"} value={quoteEditData.yearly_2x_price} onChange={(e) => setQuoteEditData({ ...quoteEditData, yearly_2x_price: e.target.value })} className="h-8 bg-[#121212] border-[#27272A] text-white text-xs flex-1" disabled={quoteEditData.yearly_2x_price === "N/A"} placeholder="0.00" />
                                    <Button type="button" size="sm" className={`h-8 px-2 text-xs ${quoteEditData.yearly_2x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} variant={quoteEditData.yearly_2x_price === "N/A" ? "default" : "ghost"} onClick={() => setQuoteEditData({ ...quoteEditData, yearly_2x_price: quoteEditData.yearly_2x_price === "N/A" ? "" : "N/A" })}>N/A</Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Status</Label>
                              <Select value={quoteEditData.status} onValueChange={(v) => setQuoteEditData({ ...quoteEditData, status: v })}>
                                <SelectTrigger className="h-8 bg-[#121212] border-[#27272A] text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#27272A]">
                                  <SelectItem value="awaiting_quote">Awaiting Quote</SelectItem>
                                  <SelectItem value="sent">Sent</SelectItem>
                                  <SelectItem value="accepted">Accepted</SelectItem>
                                  <SelectItem value="declined">Declined</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Notes</Label>
                              <Textarea
                                value={quoteEditData.notes}
                                onChange={(e) => setQuoteEditData({ ...quoteEditData, notes: e.target.value })}
                                className="h-16 bg-[#121212] border-[#27272A] text-white text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-[#A1A1AA] font-mono">{quote.quote_id}</span>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                quote.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                                quote.status === "accepted" ? "bg-green-500/20 text-green-400" :
                                quote.status === "declined" ? "bg-red-500/20 text-red-400" :
                                "bg-yellow-500/20 text-yellow-400"
                              }`}>
                                {quote.status?.replace("_", " ")}
                              </span>
                              <button
                                onClick={() => handleEditQuote(quote)}
                                className="p-1.5 text-[#A1A1AA] hover:text-[#39FF14] hover:bg-[#27272A] rounded transition-colors"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Yard Size</p>
                              <p className="text-white capitalize">{quote.yard_size === "unknown" ? "Unknown" : quote.yard_size}</p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Grass Height</p>
                              <p className="text-white capitalize">{quote.grass_height === "unknown" ? "Unknown" : quote.grass_height}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                            <div>
                              <p className="text-[#A1A1AA] text-xs">One-Time</p>
                              <p className="text-[#39FF14] font-bold">
                                {quote.quoted_price ? `$${quote.quoted_price}` : "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Monthly (1x)</p>
                              <p className="text-blue-400 font-bold">
                                {quote.monthly_1x_price ? `$${quote.monthly_1x_price}` : "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Monthly (2x)</p>
                              <p className="text-blue-300 font-bold">
                                {quote.monthly_2x_price ? `$${quote.monthly_2x_price}` : "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Yearly (1x)</p>
                              <p className="text-yellow-400 font-bold">
                                {quote.yearly_1x_price ? `$${quote.yearly_1x_price}` : "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Yearly (2x)</p>
                              <p className="text-yellow-300 font-bold">
                                {quote.yearly_2x_price ? `$${quote.yearly_2x_price}` : "N/A"}
                              </p>
                            </div>
                          </div>
                          {quote.customer_address && (
                            <p className="text-[#A1A1AA] text-xs mt-2 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {quote.customer_address}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ))
                )}
              </TabsContent>

              {/* Appointments Tab */}
              <TabsContent value="appointments" className="mt-4 space-y-3">
                {customerJobs.length === 0 ? (
                  <div className="text-center py-8 text-[#A1A1AA]">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No appointments for this customer</p>
                  </div>
                ) : (
                  customerJobs.map((job) => (
                    <div key={job.job_id} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                      {editingJob === job.job_id ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-[#A1A1AA] font-mono">{job.job_id}</span>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditingJob(null)}>
                                Cancel
                              </Button>
                              <Button size="sm" onClick={() => handleSaveJob(job.job_id)} className="bg-[#39FF14] text-black hover:bg-[#32D711]">
                                <Save className="w-3 h-3 mr-1" /> Save
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Date</Label>
                              <Input
                                type="date"
                                value={jobEditData.scheduled_date}
                                onChange={(e) => setJobEditData({ ...jobEditData, scheduled_date: e.target.value })}
                                className="h-8 bg-[#121212] border-[#27272A] text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Time</Label>
                              <Input
                                type="time"
                                value={jobEditData.scheduled_time}
                                onChange={(e) => setJobEditData({ ...jobEditData, scheduled_time: e.target.value })}
                                className="h-8 bg-[#121212] border-[#27272A] text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Price ($)</Label>
                              <Input
                                type="number"
                                value={jobEditData.price}
                                onChange={(e) => setJobEditData({ ...jobEditData, price: parseFloat(e.target.value) || 0 })}
                                className="h-8 bg-[#121212] border-[#27272A] text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#A1A1AA]">Status</Label>
                              <Select value={jobEditData.status} onValueChange={(v) => setJobEditData({ ...jobEditData, status: v })}>
                                <SelectTrigger className="h-8 bg-[#121212] border-[#27272A] text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#27272A]">
                                  <SelectItem value="scheduled">Scheduled</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-[#A1A1AA]">Notes</Label>
                            <Textarea
                              value={jobEditData.notes}
                              onChange={(e) => setJobEditData({ ...jobEditData, notes: e.target.value })}
                              className="h-16 bg-[#121212] border-[#27272A] text-white"
                            />
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-[#A1A1AA] font-mono">{job.job_id}</span>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                job.status === "completed" ? "bg-green-500/20 text-green-400" :
                                job.status === "scheduled" ? "bg-blue-500/20 text-blue-400" :
                                job.status === "in_progress" ? "bg-yellow-500/20 text-yellow-400" :
                                "bg-red-500/20 text-red-400"
                              }`}>
                                {job.status?.replace("_", " ")}
                              </span>
                              <button
                                onClick={() => handleEditJob(job)}
                                className="p-1.5 text-[#A1A1AA] hover:text-[#39FF14] hover:bg-[#27272A] rounded transition-colors"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Date</p>
                              <p className="text-white">{job.scheduled_date}</p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Time</p>
                              <p className="text-white">{job.scheduled_time}</p>
                            </div>
                            <div>
                              <p className="text-[#A1A1AA] text-xs">Price</p>
                              <p className="text-[#39FF14] font-bold">${job.price}</p>
                            </div>
                          </div>
                          {job.notes && (
                            <p className="text-[#A1A1AA] text-xs mt-2">Notes: {job.notes}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))
                )}
              </TabsContent>

              {/* Chat Tab */}
              <TabsContent value="chat" className="mt-4">
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden">
                  {/* Chat messages area */}
                  <div className="h-64 overflow-y-auto p-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-[#A1A1AA]">
                        <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No messages yet</p>
                        <p className="text-xs">Start a conversation with this customer</p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => (
                        <div
                          key={msg.message_id || idx}
                          className={`flex ${msg.sender_type === "admin" ? "justify-end" : "justify-start"} group`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                              msg.sender_type === "admin"
                                ? "bg-[#39FF14] text-black rounded-br-sm"
                                : "bg-[#27272A] text-white rounded-bl-sm"
                            }`}
                          >
                            <p className="text-sm">{msg.message}</p>
                            <div className={`flex items-center justify-between mt-1 ${msg.sender_type === "admin" ? "text-black/60" : "text-[#A1A1AA]"}`}>
                              <p className="text-xs">
                                {msg.sender_name} • {new Date(msg.created_at).toLocaleString()}
                                {msg.message_type === "follow_up" && " (Follow-up)"}
                              </p>
                              <button
                                onClick={() => handleDeleteMessage(msg.message_id)}
                                className={`ml-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/20 ${
                                  msg.sender_type === 'admin' ? 'text-black/60 hover:text-black' : 'text-[#A1A1AA] hover:text-red-400'
                                }`}
                                title="Delete message"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  
                  {/* Message input */}
                  <div className="border-t border-[#27272A] p-3 flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-[#121212] border-[#27272A] text-white"
                      onKeyPress={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      className="bg-[#39FF14] text-black hover:bg-[#32D711]"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Follow-up button */}
                  <div className="border-t border-[#27272A] p-3">
                    <Button
                      onClick={() => setFollowUpDialogOpen(true)}
                      variant="outline"
                      className="w-full border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                    >
                      <MailIcon className="w-4 h-4 mr-2" />
                      Send Follow-up Email/SMS
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Stats Tab */}
              <TabsContent value="stats" className="mt-4">
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-[#064E3B] to-[#0A0A0A] border border-[#39FF14]/30 rounded-xl p-4 text-center">
                      <Scissors className="w-8 h-8 text-[#39FF14] mx-auto mb-2" />
                      <p className="text-3xl font-bold text-[#39FF14]">{customerStats?.total_cuts_completed || 0}</p>
                      <p className="text-[#A1A1AA] text-sm">Total Cuts</p>
                    </div>
                    <div className="bg-gradient-to-br from-[#064E3B] to-[#0A0A0A] border border-[#39FF14]/30 rounded-xl p-4 text-center">
                      <DollarSign className="w-8 h-8 text-[#39FF14] mx-auto mb-2" />
                      <p className="text-3xl font-bold text-[#39FF14]">${customerStats?.total_amount_paid || 0}</p>
                      <p className="text-[#A1A1AA] text-sm">Total Paid</p>
                    </div>
                    <div className="bg-gradient-to-br from-[#0A0A0A] to-[#27272A] border border-[#27272A] rounded-xl p-4 text-center">
                      <Calendar className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-yellow-400">{customerStats?.pending_appointments || 0}</p>
                      <p className="text-[#A1A1AA] text-sm">Pending</p>
                    </div>
                  </div>

                  {/* Subscription Status */}
                  {subscriptionsEnabled && customerStats?.subscription && (
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-[#39FF14]" />
                        Subscription
                      </h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-[#A1A1AA]">Plan</p>
                          <p className="text-white capitalize">{customerStats.subscription.plan_type}</p>
                        </div>
                        <div>
                          <p className="text-[#A1A1AA]">Status</p>
                          <p className={`capitalize ${customerStats.subscription.status === "active" ? "text-green-400" : "text-yellow-400"}`}>
                            {customerStats.subscription.status}
                          </p>
                        </div>
                        <div>
                          <p className="text-[#A1A1AA]">Discount</p>
                          <p className="text-[#39FF14]">{customerStats.subscription.discount_percent}%</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recent Payment History */}
                  <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#39FF14]" />
                      Recent Payment History
                    </h4>
                    {customerStats?.job_history?.length > 0 ? (
                      <div className="space-y-2">
                        {customerStats.job_history.map((job, idx) => (
                          <div key={job.job_id || idx} className="flex items-center justify-between py-2 border-b border-[#27272A] last:border-0">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#39FF14]/20 rounded-full flex items-center justify-center">
                                <Scissors className="w-4 h-4 text-[#39FF14]" />
                              </div>
                              <div>
                                <p className="text-white text-sm">Lawn Service</p>
                                <p className="text-[#A1A1AA] text-xs">{job.date}</p>
                              </div>
                            </div>
                            <p className="text-[#39FF14] font-semibold">${job.price}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#A1A1AA] text-center py-4">No payment history yet</p>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Info Tab */}
              <TabsContent value="info" className="mt-4">
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-1">Phone</p>
                      <p className="text-white flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#39FF14]" />
                        {selectedCustomer?.phone || "Not provided"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-1">Email</p>
                      <p className="text-white flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#39FF14]" />
                        {selectedCustomer?.email}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-xs mb-1">Address</p>
                    <p className="text-white flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-[#39FF14] mt-0.5" />
                      {selectedCustomer?.address || "Not provided"}
                    </p>
                  </div>
                  {selectedCustomer?.notes && (
                    <div>
                      <p className="text-[#A1A1AA] text-xs mb-1">Notes</p>
                      <p className="text-white">{selectedCustomer.notes}</p>
                    </div>
                  )}
                  <div className="pt-4 border-t border-[#27272A]">
                    <Button
                      onClick={() => {
                        setViewDialogOpen(false);
                        handleEdit(selectedCustomer);
                      }}
                      className="w-full bg-[#39FF14] text-black hover:bg-[#32D711]"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit Customer Details
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog open={followUpDialogOpen} onOpenChange={setFollowUpDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MailIcon className="w-5 h-5 text-[#39FF14]" />
              Send Follow-up
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">To</Label>
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-md p-3 mt-1">
                <p className="text-white font-medium">{selectedCustomer?.name}</p>
                <div className="flex gap-4 text-sm text-[#A1A1AA] mt-1">
                  {selectedCustomer?.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {selectedCustomer.email}</span>}
                  {selectedCustomer?.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {selectedCustomer.phone}</span>}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Subject (for email)</Label>
              <Input
                value={followUpData.subject}
                onChange={(e) => setFollowUpData({ ...followUpData, subject: e.target.value })}
                placeholder="e.g., Your Zig Zag Lawncare Quote"
                className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
              />
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Message</Label>
              <Textarea
                value={followUpData.message}
                onChange={(e) => setFollowUpData({ ...followUpData, message: e.target.value })}
                placeholder="Type your message here..."
                className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white min-h-[120px]"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[#A1A1AA]">Send via</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={followUpData.send_email}
                    onCheckedChange={(checked) => setFollowUpData({ ...followUpData, send_email: checked })}
                    className="border-[#39FF14] data-[state=checked]:bg-[#39FF14]"
                  />
                  <span className="text-white flex items-center gap-1">
                    <Mail className="w-4 h-4" /> Email
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={followUpData.send_sms}
                    onCheckedChange={(checked) => setFollowUpData({ ...followUpData, send_sms: checked })}
                    className="border-[#39FF14] data-[state=checked]:bg-[#39FF14]"
                  />
                  <span className="text-white flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" /> SMS
                  </span>
                </label>
              </div>
              <p className="text-xs text-yellow-500">Note: Email/SMS delivery requires API keys to be configured.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowUpDialogOpen(false)} className="border-[#27272A]">
              Cancel
            </Button>
            <Button
              onClick={handleSendFollowUp}
              disabled={sendingFollowUp || !followUpData.message.trim()}
              className="bg-[#39FF14] text-black hover:bg-[#32D711]"
            >
              {sendingFollowUp ? "Sending..." : "Send Follow-up"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Chat Dialog */}
      <Dialog open={quickChatOpen} onOpenChange={setQuickChatOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-[#39FF14]" />
              Chat with {quickChatCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col h-[400px]">
            {/* Messages List */}
            <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-[#0A0A0A] rounded-lg mb-4">
              {quickChatMessages.length === 0 ? (
                <div className="text-center text-[#A1A1AA] py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start a conversation with {quickChatCustomer?.name}</p>
                </div>
              ) : (
                quickChatMessages.map((msg, idx) => (
                  <div
                    key={msg.message_id || idx}
                    className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'} group`}
                  >
                    <div className={`max-w-[80%] rounded-lg px-4 py-2 relative ${
                      msg.sender_type === 'admin'
                        ? 'bg-[#39FF14] text-black'
                        : 'bg-[#27272A] text-white'
                    }`}>
                      <p className="text-sm">{msg.message}</p>
                      <div className={`flex items-center justify-between mt-1 ${msg.sender_type === 'admin' ? 'text-black/60' : 'text-[#A1A1AA]'}`}>
                        <p className="text-xs">
                          {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <button
                          onClick={() => handleDeleteQuickChatMessage(msg.message_id)}
                          className={`ml-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/20 ${
                            msg.sender_type === 'admin' ? 'text-black/60 hover:text-black' : 'text-[#A1A1AA] hover:text-red-400'
                          }`}
                          title="Delete message"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={quickChatEndRef} />
            </div>
            
            {/* Message Input */}
            <div className="flex gap-2">
              <Textarea
                value={quickChatNewMessage}
                onChange={(e) => setQuickChatNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[60px] max-h-[100px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleQuickChatSend();
                  }
                }}
              />
              <Button
                onClick={handleQuickChatSend}
                disabled={quickChatSending || !quickChatNewMessage.trim()}
                className="bg-[#39FF14] text-black hover:bg-[#32D711] px-4"
              >
                {quickChatSending ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Prices Dialog */}
      <Dialog open={priceEditOpen} onOpenChange={setPriceEditOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#39FF14]" />
              Edit Prices
            </DialogTitle>
          </DialogHeader>
          
          {priceEditCustomer && (
            <div className="space-y-4">
              <p className="text-[#A1A1AA] text-sm">
                Update quote for <span className="text-white font-medium">{priceEditCustomer.name}</span>
              </p>
              
              {/* Yard Size & Grass Height */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm text-[#A1A1AA]">Yard Size</label>
                  <Select value={priceEditData.yard_size} onValueChange={(v) => setPriceEditData(prev => ({ ...prev, yard_size: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      <SelectItem value="small" className="text-white">Small</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="large" className="text-white">Large</SelectItem>
                      <SelectItem value="extra-large" className="text-white">Extra Large</SelectItem>
                      <SelectItem value="unknown" className="text-white">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm text-[#A1A1AA]">Grass Height</label>
                  <Select value={priceEditData.grass_height} onValueChange={(v) => setPriceEditData(prev => ({ ...prev, grass_height: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      <SelectItem value="short" className="text-white">Short</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="tall" className="text-white">Tall</SelectItem>
                      <SelectItem value="overgrown" className="text-white">Overgrown</SelectItem>
                      <SelectItem value="unknown" className="text-white">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-1">
                <label className="text-sm text-[#A1A1AA]">Address</label>
                <Input
                  placeholder="Property address"
                  value={priceEditData.customer_address}
                  onChange={(e) => setPriceEditData(prev => ({ ...prev, customer_address: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                />
              </div>

              {/* One-Time Cut Price */}
              <div className="space-y-1">
                <label className="text-sm text-[#A1A1AA]">One-Time Cut Price</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#39FF14]" />
                    <Input
                      type={priceEditData.quoted_price === "N/A" ? "text" : "number"}
                      step="0.01"
                      placeholder="0.00"
                      value={priceEditData.quoted_price}
                      onChange={(e) => setPriceEditData(prev => ({ ...prev, quoted_price: e.target.value }))}
                      className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                      disabled={priceEditData.quoted_price === "N/A"}
                      data-testid="edit-one-time-price"
                    />
                  </div>
                  <Button
                    type="button"
                    variant={priceEditData.quoted_price === "N/A" ? "default" : "ghost"}
                    className={`px-3 ${priceEditData.quoted_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`}
                    onClick={() => setPriceEditData(prev => ({ ...prev, quoted_price: prev.quoted_price === "N/A" ? "" : "N/A" }))}
                  >N/A</Button>
                </div>
              </div>
              
              {/* Monthly Plan */}
              <div className="space-y-2 pt-2 border-t border-[#27272A]">
                <label className="text-sm text-[#A1A1AA] font-semibold uppercase">Monthly Plan</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-[#A1A1AA]">1 Cut/Month</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-400" />
                        <Input
                          type={priceEditData.monthly_1x_price === "N/A" ? "text" : "number"}
                          step="0.01"
                          placeholder="0.00"
                          value={priceEditData.monthly_1x_price}
                          onChange={(e) => setPriceEditData(prev => ({ ...prev, monthly_1x_price: e.target.value }))}
                          className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                          disabled={priceEditData.monthly_1x_price === "N/A"}
                          data-testid="edit-monthly-1x-price"
                        />
                      </div>
                      <Button type="button" variant={priceEditData.monthly_1x_price === "N/A" ? "default" : "ghost"} className={`px-3 ${priceEditData.monthly_1x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setPriceEditData(prev => ({ ...prev, monthly_1x_price: prev.monthly_1x_price === "N/A" ? "" : "N/A" }))}>N/A</Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-[#A1A1AA]">2 Cuts/Month</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300" />
                        <Input
                          type={priceEditData.monthly_2x_price === "N/A" ? "text" : "number"}
                          step="0.01"
                          placeholder="0.00"
                          value={priceEditData.monthly_2x_price}
                          onChange={(e) => setPriceEditData(prev => ({ ...prev, monthly_2x_price: e.target.value }))}
                          className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                          disabled={priceEditData.monthly_2x_price === "N/A"}
                          data-testid="edit-monthly-2x-price"
                        />
                      </div>
                      <Button type="button" variant={priceEditData.monthly_2x_price === "N/A" ? "default" : "ghost"} className={`px-3 ${priceEditData.monthly_2x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setPriceEditData(prev => ({ ...prev, monthly_2x_price: prev.monthly_2x_price === "N/A" ? "" : "N/A" }))}>N/A</Button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Yearly Plan */}
              <div className="space-y-2 pt-2 border-t border-[#27272A]">
                <label className="text-sm text-[#A1A1AA] font-semibold uppercase">Yearly Plan</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-[#A1A1AA]">1 Cut/Month</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-yellow-400" />
                        <Input
                          type={priceEditData.yearly_1x_price === "N/A" ? "text" : "number"}
                          step="0.01"
                          placeholder="0.00"
                          value={priceEditData.yearly_1x_price}
                          onChange={(e) => setPriceEditData(prev => ({ ...prev, yearly_1x_price: e.target.value }))}
                          className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                          disabled={priceEditData.yearly_1x_price === "N/A"}
                          data-testid="edit-yearly-1x-price"
                        />
                      </div>
                      <Button type="button" variant={priceEditData.yearly_1x_price === "N/A" ? "default" : "ghost"} className={`px-3 ${priceEditData.yearly_1x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setPriceEditData(prev => ({ ...prev, yearly_1x_price: prev.yearly_1x_price === "N/A" ? "" : "N/A" }))}>N/A</Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-[#A1A1AA]">2 Cuts/Month</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-yellow-300" />
                        <Input
                          type={priceEditData.yearly_2x_price === "N/A" ? "text" : "number"}
                          step="0.01"
                          placeholder="0.00"
                          value={priceEditData.yearly_2x_price}
                          onChange={(e) => setPriceEditData(prev => ({ ...prev, yearly_2x_price: e.target.value }))}
                          className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                          disabled={priceEditData.yearly_2x_price === "N/A"}
                          data-testid="edit-yearly-2x-price"
                        />
                      </div>
                      <Button type="button" variant={priceEditData.yearly_2x_price === "N/A" ? "default" : "ghost"} className={`px-3 ${priceEditData.yearly_2x_price === "N/A" ? "bg-[#39FF14] text-black" : "text-[#A1A1AA]"}`} onClick={() => setPriceEditData(prev => ({ ...prev, yearly_2x_price: prev.yearly_2x_price === "N/A" ? "" : "N/A" }))}>N/A</Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <label className="text-sm text-[#A1A1AA]">Notes</label>
                <Textarea
                  placeholder="Any notes about this quote..."
                  value={priceEditData.notes}
                  onChange={(e) => setPriceEditData(prev => ({ ...prev, notes: e.target.value }))}
                  className="h-16 bg-[#0A0A0A] border-[#27272A] text-white text-sm"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
                  onClick={() => setPriceEditOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePrices}
                  disabled={savingPrices}
                  className="flex-1 bg-[#39FF14] hover:bg-[#32E512] text-black font-bold"
                  data-testid="save-prices-btn"
                >
                  {savingPrices ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Prices
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Send Email</DialogTitle>
          </DialogHeader>
          {emailTarget && (
            <div className="space-y-4">
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3">
                <p className="text-[#A1A1AA] text-xs">To</p>
                <p className="text-white font-medium">{emailTarget.name} &lt;{emailTarget.email}&gt;</p>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Subject</Label>
                <Input
                  data-testid="email-subject-input"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Message</Label>
                <Textarea
                  data-testid="email-body-input"
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white min-h-[200px] resize-none"
                />
              </div>
              <div className="flex justify-between items-center">
                <p className="text-[#52525B] text-xs">Sent from zigzaglawn@gmail.com</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-[#27272A] text-[#A1A1AA]"
                    onClick={() => setEmailDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-[#1877F2] text-white hover:bg-[#166FE5] font-bold"
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    data-testid="send-email-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingEmail ? "Sending..." : "Send Email"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
